/**
 * Splash de marca — 3 segundos, una vez, siempre saltable.
 *
 * Un splash es tiempo que le quitas a alguien antes de dejarle usar el
 * producto, y GU-AUTH-05 promete un onboarding de 90 segundos. Por eso las
 * tres reglas son estructurales, no preferencias:
 *
 * 1. **Una sola vez por dispositivo.** La marca se presenta; no cobra peaje
 *    en cada apertura.
 * 2. **Siempre saltable**, con un toque en cualquier parte.
 * 3. **Nunca bloquea.** Si el video no carga, si el navegador se niega a
 *    reproducirlo, o si `ended` no llega nunca —pasa cuando el sistema
 *    pausa el video en segundo plano—, la app arranca igual.
 *
 * El video original dura 20 s y pesa 7.8 MB. Aquí van los últimos 3.3 s
 * —la mascota resolviendo en el logotipo— a 68 KB.
 */

import { useEffect, useRef, useState } from 'react';

export const SPLASH_VISTO_KEY = 'gu.splash_visto';

/**
 * Tope duro. Si el video no termina por su cuenta en este tiempo, se acaba
 * el splash igual: una app que no arranca es peor que una sin intro.
 */
const TOPE_MS = 5_000;

export interface SplashIntroProps {
  onDone: () => void;
}

export function SplashIntro({ onDone }: SplashIntroProps) {
  const [visible, setVisible] = useState(() => {
    try {
      return localStorage.getItem(SPLASH_VISTO_KEY) === null;
    } catch {
      // Almacenamiento bloqueado (Safari privado). Se enseña: repetir el
      // splash es mejor que no arrancar.
      return true;
    }
  });

  // `onDone` en una ref para que el efecto del tope no se reinicie cuando
  // el padre reconstruye el callback en cada render.
  const done = useRef(onDone);
  done.current = onDone;

  const terminar = useRef(() => {
    try {
      localStorage.setItem(SPLASH_VISTO_KEY, '1');
    } catch {
      /* sin persistencia: se verá otra vez, y no pasa nada */
    }
    setVisible(false);
    done.current();
  });

  /**
   * Guarda contra el doble montaje de React Strict Mode.
   *
   * En desarrollo React monta, desmonta y remonta cada componente. Al
   * desmontarse, el `<video>` es arrancado del DOM y el navegador dispara
   * `error` sobre él — que este componente leía como "el video falló,
   * termina". Resultado: el splash se cerraba en el primer frame y en
   * producción no se veía nunca.
   *
   * `montadoDesde` marca cuándo empezó ESTA instancia. Un error que llega
   * en los primeros milisegundos es el del ciclo de Strict Mode, no un
   * fallo real de carga: se ignora. Un fallo de verdad tarda más (una
   * petición de red tiene que fallar primero) y sí cierra el splash.
   */
  const montadoDesde = useRef(0);
  const GRACIA_MS = 250;

  useEffect(() => {
    if (!visible) {
      done.current();
      return;
    }
    montadoDesde.current = Date.now();
    const t = setTimeout(() => terminar.current(), TOPE_MS);
    return () => clearTimeout(t);
  }, [visible]);

  /** Un error dentro de la ventana de gracia es ruido del remontaje. */
  const alFallar = () => {
    if (Date.now() - montadoDesde.current < GRACIA_MS) return;
    terminar.current();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="splash"
      onClick={() => terminar.current()}
      style={{
        position: 'fixed',
        inset: 0,
        // El cream de la app, no negro: el video tiene fondo claro y un
        // borde oscuro alrededor se vería como un error de recorte.
        background: 'var(--gu-surface-app)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 9999,
        cursor: 'pointer',
      }}
    >
      <video
        data-testid="splash-video"
        src="/brand/intro.mp4"
        poster="/brand/intro-poster.jpg"
        autoPlay
        // `muted` no es solo cortesía: es lo que hace que iOS permita el
        // autoplay. Sin él, el video no arranca y el splash se queda en el
        // póster hasta el tope.
        muted
        playsInline
        onEnded={() => terminar.current()}
        onError={alFallar}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: '28rem',
        }}
      />

      <p
        style={{
          position: 'absolute',
          bottom: 'var(--gu-space-6)',
          margin: 0,
          fontSize: 'var(--gu-text-caption)',
          color: 'var(--gu-text-secondary)',
        }}
      >
        Toca para saltar
      </p>
    </div>
  );
}
